import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Button, Card, DataBoundaryBar, EmptyState, PageHeader } from '@/components/ui'

/**
 * R40 and R48: a partner sees and exports their own results. This is part of
 * what the subscription buys, and it is also the practical proof that the
 * data remains theirs.
 */
export function ReportsPage() {
  const { data: counts, isLoading } = useQuery({
    queryKey: ['submission-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('evaluation_submission_counts')
        .select('form_title, submitted_count, draft_count, last_submitted_at')
      if (error) throw error
      return data
    },
  })

  async function exportCsv() {
    const { data, error } = await supabase
      .from('evaluation_submissions')
      .select('id, cohort_ref, participant_ref, status, submitted_at, answers')
      .order('created_at', { ascending: false })
    if (error || !data) return

    const keys = Array.from(
      new Set(data.flatMap((row) => Object.keys((row.answers ?? {}) as object))),
    )
    const header = ['id', 'cohort_ref', 'participant_ref', 'status', 'submitted_at', ...keys]
    const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`
    const lines = [
      header.join(','),
      ...data.map((row) =>
        [
          row.id,
          row.cohort_ref,
          row.participant_ref,
          row.status,
          row.submitted_at,
          ...keys.map((k) => (row.answers as Record<string, unknown>)?.[k]),
        ]
          .map(escape)
          .join(','),
      ),
    ]

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `evaluation-export-${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  return (
    <>
      <PageHeader title="Your results">
        Your own evaluation data, for your reporting and funding obligations.
      </PageHeader>

      <DataBoundaryBar />

      {isLoading && <p className="text-sm text-inksoft">Loading…</p>}

      {!isLoading && (counts ?? []).length === 0 && (
        <EmptyState
          title="No results yet"
          body="Results appear here as your team records sessions and participant surveys."
        />
      )}

      {(counts ?? []).length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            {(counts ?? []).map((row) => (
              <Card key={row.form_title}>
                <p className="ref">{row.form_title}</p>
                <p className="mt-2 font-display text-3xl text-pounamu">{row.submitted_count}</p>
                <p className="text-sm text-inksoft">
                  submitted{row.draft_count ? ` · ${row.draft_count} in draft` : ''}
                </p>
              </Card>
            ))}
          </div>
          <div className="mt-6">
            <Button onClick={exportCsv} variant="secondary">Export your data as CSV</Button>
          </div>
        </>
      )}
    </>
  )
}
